import React from 'react';
import { useAuthContext } from '@/contexts/use-auth-context';
import { isStatsigConfigured, StatsigAdapter } from '@/providers/adapters';
import { FeatureFlagContext } from '@/providers/feature-flag-context';
import type { FeatureFlagContextValue } from '@/types/context/feature-flag-context.types';

interface FeatureFlagProviderProps {
  children: React.ReactNode;
}

/** Default value when feature flags are disabled */
const DISABLED_VALUE: FeatureFlagContextValue = {
  isReady: true,
  isFeatureEnabled: () => false,
};

/**
 * Feature flag provider with full abstraction.
 *
 * This provider is stable - to switch feature flag libraries,
 * only change the adapter in `@/providers/adapters/`.
 *
 * IMPORTANT: Must be rendered inside AuthProvider to access user context.
 */
export function FeatureFlagProvider({ children }: FeatureFlagProviderProps) {
  const { user, tenantSlug } = useAuthContext();

  // No adapter configured = disabled mode (all flags false)
  if (!isStatsigConfigured()) {
    return (
      <FeatureFlagContext.Provider value={DISABLED_VALUE}>
        {children}
      </FeatureFlagContext.Provider>
    );
  }

  return (
    <StatsigAdapter userId={user?.id} tenantSlug={tenantSlug}>
      {value => (
        <FeatureFlagContext.Provider value={value}>
          {children}
        </FeatureFlagContext.Provider>
      )}
    </StatsigAdapter>
  );
}
